import { Platform } from 'react-native';

export interface HapticsInterface {
  impact: (style: 'light' | 'medium' | 'heavy') => void;
  notification: (type: 'success' | 'warning' | 'error') => void;
  selection: () => void;
}

export function useHaptics(): HapticsInterface {
  const importHaptics = async () => {
    // Dynamically import to avoid SSR/Next trying to parse TS source from expo-haptics
    const mod = await import('expo-haptics');
    return mod;
  };

  const impact = async (style: 'light' | 'medium' | 'heavy') => {
    try {
      if (Platform.OS === 'ios') {
        const Haptics = await importHaptics();
        switch (style) {
          case 'light':
            await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
            break;
          case 'medium':
            await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
            break;
          case 'heavy':
            await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy);
            break;
        }
      } else if (Platform.OS === 'android') {
        const Haptics = await importHaptics();
        // Android haptic feedback with proper style mapping
        switch (style) {
          case 'light':
            await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
            break;
          case 'medium':
            await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
            break;
          case 'heavy':
            await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy);
            break;
        }
      } else if (
        Platform.OS === 'web' &&
        typeof navigator !== 'undefined' &&
        'vibrate' in navigator
      ) {
        // Basic web vibration as a fallback on supported browsers
        const duration = style === 'heavy' ? 50 : style === 'medium' ? 30 : 15;
        navigator.vibrate?.(duration);
      }
    } catch (error) {
      // Silently handle haptic feedback errors to prevent app crashes
      console.warn('Haptic feedback failed:', error);
    }
  };

  const notification = async (type: 'success' | 'warning' | 'error') => {
    try {
      if (Platform.OS === 'ios') {
        const Haptics = await importHaptics();
        switch (type) {
          case 'success':
            await Haptics.notificationAsync(
              Haptics.NotificationFeedbackType.Success
            );
            break;
          case 'warning':
            await Haptics.notificationAsync(
              Haptics.NotificationFeedbackType.Warning
            );
            break;
          case 'error':
            await Haptics.notificationAsync(
              Haptics.NotificationFeedbackType.Error
            );
            break;
        }
      } else if (Platform.OS === 'android') {
        const Haptics = await importHaptics();
        // Android notification feedback - use impact as fallback
        await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
      } else if (
        Platform.OS === 'web' &&
        typeof navigator !== 'undefined' &&
        'vibrate' in navigator
      ) {
        // Basic web vibration pattern
        navigator.vibrate?.(
          type === 'error'
            ? [30, 40, 30]
            : type === 'warning'
              ? [20, 20, 20]
              : 20
        );
      }
    } catch (error) {
      // Silently handle haptic feedback errors to prevent app crashes
      console.warn('Haptic notification failed:', error);
    }
  };

  const selection = async () => {
    try {
      if (Platform.OS === 'ios') {
        const Haptics = await importHaptics();
        await Haptics.selectionAsync();
      } else if (Platform.OS === 'android') {
        const Haptics = await importHaptics();
        // Android selection feedback - use light impact as fallback
        await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      } else if (
        Platform.OS === 'web' &&
        typeof navigator !== 'undefined' &&
        'vibrate' in navigator
      ) {
        navigator.vibrate?.(10);
      }
    } catch (error) {
      // Silently handle haptic feedback errors to prevent app crashes
      console.warn('Haptic selection failed:', error);
    }
  };

  return {
    impact,
    notification,
    selection,
  };
}
